/**
 * PrintingImagesCarousel component
 * Displays event-photo images in an interactive carousel with thumbnails
 *
 * Features:
 * - Main carousel with zoom and navigation
 * - Thumbnail carousel for quick navigation
 * - Event ID badges with links to event details
 * - Lazy loading for optimal performance
 * - Pagination support for large image sets
 */

import { useEffect, useState, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, Zoom } from 'swiper/modules';
import { Box, styled } from '@mui/material';
import { Event } from '../../types';
import type { Swiper as SwiperType } from 'swiper';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';

// Placeholder image for lazy loading
const imagePlaceholder =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4MCIgaGVpZ2h0PSI3MjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2U5ZWNlZiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM3ODdhODEiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';

// Styled components
const CarouselContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  paddingBottom: theme.spacing(0.5),
  backgroundColor: '#fafafa',
}));

const MainSwiper = styled(Swiper)(({ theme }) => ({
  '--swiper-navigation-size': '32px',
  '--swiper-theme-color': theme.palette.info.main,
}));

const ThumbsSwiper = styled(Swiper)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(2.25),
  '--swiper-theme-color': theme.palette.info.main,
  '& .swiper-slide-thumb-active': {
    boxShadow: `0 0 4px 3px ${theme.palette.info.main}`,
  },
  '& .swiper-scrollbar': {
    height: 10,
  },
}));

const SlideImage = styled('img')({
  display: 'block',
  height: 'auto',
  maxWidth: '100%',
});

const EventIdBadge = styled('span')(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  fontSize: 12,
  padding: '4px 8px',
  margin: theme.spacing(1),
  background: theme.palette.info.main,
  color: '#fff',
  zIndex: 1,
  borderRadius: 9999,
  opacity: 1,
  transition: 'opacity 150ms ease-in-out',
  transitionDelay: '400ms',
  '& a, & a:hover': {
    color: '#fff',
    textDecoration: 'none',
  },
  '.swiper-slide-zoomed &': {
    transitionDelay: '0ms',
    transitionDuration: '0ms',
    opacity: 0,
  },
}));

const ImageIndex = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  fontSize: 10,
  padding: '4px 8px',
  borderRadius: 999,
  background: theme.palette.info.main,
  color: '#fff',
  opacity: 0.85,
  margin: theme.spacing(0.25),
}));

export interface PrintingImagesCarouselProps {
  data?: Event[];
  index?: number;
  onSlideChange?: (index: number) => void;
  onPageChange?: () => void;
}

export const PrintingImagesCarousel = ({
  data = [],
  index = -1,
  onSlideChange,
  onPageChange = () => null,
}: PrintingImagesCarouselProps) => {
  const [width, setWidth] = useState(-1);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const mainSwiperRef = useRef<SwiperType | null>(null);
  const thumbsSwiperRef = useRef<SwiperType | null>(null);

  // Handle window resize for responsive carousel
  useEffect(() => {
    const calculateWidth = () => {
      setWidth(0); // Reset width
      if (boxRef.current) {
        setWidth(boxRef.current.clientWidth);
      }
    };

    const resizeHandler = () => {
      // Debounce resize handler
      let timeoutId: NodeJS.Timeout;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(calculateWidth, 200);
      };
    };

    const debouncedResize = resizeHandler();

    if (boxRef.current) {
      calculateWidth();
    }

    window.addEventListener('resize', debouncedResize);

    return () => window.removeEventListener('resize', debouncedResize);
  }, []);

  // Update Swiper instances when width changes
  useEffect(() => {
    if (width > 0) {
      mainSwiperRef.current?.update();
      thumbsSwiperRef.current?.update();
    }
  }, [width]);

  // Navigate to specific slide when index prop changes
  useEffect(() => {
    if (index >= 0) {
      mainSwiperRef.current?.slideTo(index);
      thumbsSwiperRef.current?.slideTo(index);
    }
  }, [index]);

  if (!data.length) return null;

  return (
    <CarouselContainer ref={boxRef}>
      {width > -1 ? (
        <>
          {/* Main Swiper with zoom and navigation */}
          <MainSwiper
            modules={[Navigation, Thumbs, Zoom]}
            style={{ width }}
            initialSlide={index >= 0 ? index : 0}
            thumbs={{ swiper: thumbsSwiper }}
            onSwiper={(swiper) => {
              mainSwiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => {
              if (onSlideChange) {
                onSlideChange(swiper.activeIndex);
              }
            }}
            onReachEnd={onPageChange}
            resistanceRatio={0.95}
            zoom={true}
            navigation={true}
          >
            {data.map((event) => (
              <SwiperSlide key={event.id}>
                <div className="swiper-zoom-container">
                  <EventIdBadge>
                    <a href={`#/events/${event.id}/show`}>{event.id}</a>
                  </EventIdBadge>
                  <SlideImage src={event.photoUrl || imagePlaceholder} alt={`Event ${event.id}`} />
                </div>
              </SwiperSlide>
            ))}
          </MainSwiper>

          {/* Thumbnail Swiper for quick navigation */}
          <ThumbsSwiper
            modules={[Thumbs]}
            style={{ width }}
            onSwiper={(swiper) => {
              thumbsSwiperRef.current = swiper;
              setThumbsSwiper(swiper);
            }}
            onReachEnd={onPageChange}
            watchSlidesProgress
            scrollbar={{ draggable: true }}
            centeredSlides={true}
            centeredSlidesBounds={true}
            spaceBetween={8}
            resistanceRatio={2}
            breakpoints={{
              320: {
                slidesPerView: 3,
              },
              480: {
                slidesPerView: 4,
              },
              990: {
                slidesPerView: 6,
              },
              1280: {
                slidesPerView: 8,
              },
            }}
          >
            {data.map((event, eventIndex) => (
              <SwiperSlide key={event.id}>
                <SlideImage
                  src={event.photoUrl || imagePlaceholder}
                  alt={`Event ${event.id} thumbnail`}
                />
                <ImageIndex>{eventIndex}</ImageIndex>
              </SwiperSlide>
            ))}
          </ThumbsSwiper>
        </>
      ) : null}
    </CarouselContainer>
  );
};

export default PrintingImagesCarousel;
